package co.maytes.standupbot.dashboard.pages

import androidx.compose.runtime.Composable
import co.maytes.standupbot.dashboard.components.layout.MarketingShell
import co.maytes.standupbot.dashboard.components.marketing.MkIcon
import co.maytes.standupbot.dashboard.components.marketing.PageIntro
import co.maytes.standupbot.dashboard.components.motion.Reveal
import co.maytes.standupbot.dashboard.styles.Marketing
import co.maytes.standupbot.dashboard.styles.MarketingIcon
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.core.Page
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Section
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul

@Page("/changelog")
@Composable
fun ChangelogPage() {
    MarketingShell {
        Div(attrs = { classes(Marketing.PAGE_CLASS) }) {
            PageIntro(
                title = "What's new.",
                line = "Release notes since the public beta.",
            )
            TimelineSection()
        }
    }
}

private data class Release(
    val version: String,
    val date: String,
    val title: String,
    val tags: List<String>,
    val bullets: List<String>,
)

private val releases: List<Release> = listOf(
    Release(
        version = "v0.6.0",
        date = "2026-10-23",
        title = "A redesigned app",
        tags = listOf("Dashboard", "Design", "Mobile"),
        bullets = listOf(
            "Settings rebuilt around one navigation, with search and a page for every topic.",
            "A graphic Home with your tools, setup progress, next standup and recent runs.",
            "A new onboarding wizard with a clear step in each stage.",
            "Logos for Slack, Linear, GitHub and Google Calendar across integrations.",
            "Members, rows and tables lay out properly on phones.",
            "Loading, empty and error states now change with a short animation.",
        ),
    ),
    Release(
        version = "v0.5.0",
        date = "2026-10-22",
        title = "Access, exports and releases",
        tags = listOf("Security", "Slack", "GitHub"),
        bullets = listOf(
            "Disable, restore or erase a member, and transfer ownership.",
            "Members who are deactivated in Slack lose access automatically.",
            "Owners can download a full workspace export; admins can export the audit log.",
            "Choose how long raw activity is kept, and delete a workspace with 30 days to recover.",
            "GitHub releases appear in the release feed.",
            "/standup blockers, a meeting link on the post, and a note when your first standup posts.",
            "Pick Claude Sonnet 5.5 or Haiku 5.5 for your own Anthropic key.",
        ),
    ),
    Release(
        version = "v0.4.0",
        date = "2026-10-19",
        title = "Review before it posts",
        tags = listOf("Slack", "Drafts"),
        bullets = listOf(
            "Get your update as a draft DM, then approve, edit or skip it.",
            "The dashboard shows draft status and lets you review drafts.",
        ),
    ),
    Release(
        version = "v0.3.0",
        date = "2026-08-13",
        title = "Bring your own key",
        tags = listOf("Billing", "Claude"),
        bullets = listOf(
            "Pro and Business run Claude on your own Anthropic account.",
            "Run history is kept for as long as your plan allows.",
            "A guide to creating your Anthropic key.",
        ),
    ),
    Release(
        version = "v0.2.0",
        date = "2026-06-25",
        title = "Summaries and a polished app",
        tags = listOf("Dashboard", "Summaries"),
        bullets = listOf(
            "A manager summary of what the team shipped.",
            "Accomplishment summaries for any window.",
            "Skeleton loading and friendlier empty states across the app.",
        ),
    ),
    Release(
        version = "v0.1.1",
        date = "2026-05-05",
        title = "Invites and logs",
        tags = listOf("Members", "Logs"),
        bullets = listOf(
            "Invite teammates by link and resend invites from the dashboard.",
            "Link Linear and GitHub accounts to members in settings.",
            "A system logs page for admins, and a prompt archive for every run.",
        ),
    ),
    Release(
        version = "v0.1.0",
        date = "2026-04-21",
        title = "Public beta launch",
        tags = listOf("Launch", "Slack", "Linear", "Claude"),
        bullets = listOf(
            "Slack OAuth install for any workspace admin.",
            "Linear activity pull for assignees, transitions and PR references.",
            "Claude synthesis with fallback summaries when tokens are tight.",
            "Dashboard settings for standups, schedule, channel and prompt preview.",
            "Cron scheduling that delivers in your timezone.",
        ),
    ),
    Release(
        version = "v0.0.9",
        date = "2026-04-10",
        title = "Dashboard foundation",
        tags = listOf("Dashboard", "Onboarding"),
        bullets = listOf(
            "Dashboard shell with shared design tokens and dark mode.",
            "Onboarding wizard for Slack and Linear setup.",
            "An integrations screen to revoke, reauthorize and rotate.",
        ),
    ),
    Release(
        version = "v0.0.8",
        date = "2026-03-28",
        title = "Backend MVP",
        tags = listOf("Backend"),
        bullets = listOf(
            "Ktor service with Flyway migrations and Koin DI.",
            "Domain model for teams, standups, activity and token accounting.",
            "Postgres repositories on Supabase with UTC-only timestamps.",
        ),
    ),
)

@Composable
private fun TimelineSection() {
    Section(attrs = { classes("mk-section", "mk-section--tight") }) {
        Div(attrs = { classes("mk-wrap", "mk-narrow") }) {
            Ol(attrs = { classes("mk-timeline") }) {
                releases.forEachIndexed { index, release ->
                    Li(attrs = { classes("mk-tl-item"); if (index == 0) classes("mk-tl-item--latest") }) {
                        Span(attrs = { classes("mk-tl-node"); attr("aria-hidden", "true") })
                        Reveal(index = index, modifier = Modifier.classNames("mk-tl-card")) { ReleaseCard(release) }
                    }
                }
            }
            P(attrs = { classes("mk-tl-end") }) { Text("Older releases live in git history.") }
        }
    }
}

@Composable
private fun ReleaseCard(release: Release) {
    Div(attrs = { classes("mk-tl-meta") }) {
        Span(attrs = { classes("mk-version") }) { Text(release.version) }
        Span(attrs = { classes("mk-date") }) { Text(release.date) }
    }
    H2(attrs = { classes("mk-tl-title") }) { Text(release.title) }
    Div(attrs = { classes("mk-tags") }) {
        release.tags.forEach { tag -> Span(attrs = { classes("mk-tag") }) { Text(tag) } }
    }
    Ul(attrs = { classes("mk-tl-list") }) {
        release.bullets.forEach { bullet ->
            Li {
                MkIcon(MarketingIcon.Check)
                Span { Text(bullet) }
            }
        }
    }
}
